/**
 * useTokenRefresh Hook
 * Handles proactive token refresh before expiration
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getAccessToken,
  getTokenExpiresIn,
  refreshAccessToken,
  isRefreshInProgress,
} from '@/utils/token';
import { getSessionOwner, ownsSession } from '@/utils/session';
import { useAuthStore } from '@/store/auth.store';
import * as authApi from '@/api/auth.api';

/**
 * Time before expiration to trigger refresh (5 minutes)
 */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * Minimum interval between refresh checks (30 seconds)
 */
const CHECK_INTERVAL_MS = 30 * 1000;

/**
 * useTokenRefresh Hook
 *
 * Monitors the access token and proactively refreshes it before expiration.
 * Uses a single interval to check expiration and triggers refresh when needed.
 *
 * Features:
 * - Proactive refresh before token expires
 * - Deduplication of refresh requests
 * - Updates auth store with new tokens
 * - Logs out user if refresh fails
 *
 * @example
 * ```tsx
 * // In your App or AuthProvider
 * function App() {
 *   useTokenRefresh();
 *   return <Routes />;
 * }
 * ```
 */
export const useTokenRefresh = () => {
  const { isAuthenticated, updateTokens, logout } = useAuthStore();
  const queryClient = useQueryClient();
  const isRefreshingRef = useRef<number | null>(null);

  const performRefresh = useCallback(async () => {
    // Skip if already refreshing or refresh is in progress elsewhere
    const owner = getSessionOwner();
    if (isRefreshingRef.current === owner || isRefreshInProgress()) {
      return;
    }

    isRefreshingRef.current = owner;

    try {
      const tokens = await refreshAccessToken(async (refreshToken) => {
        const response = await authApi.refreshToken(refreshToken);
        return {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        };
      });

      if (!ownsSession(owner)) return;
      // Update store with new tokens
      updateTokens({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });
    } catch {
      if (!ownsSession(owner)) return;
      // Logout user on refresh failure - clear React Query cache
      // to prevent stale data leaking to the next user session
      logout();
      queryClient.clear();
    } finally {
      if (isRefreshingRef.current === owner) isRefreshingRef.current = null;
    }
  }, [updateTokens, logout, queryClient]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const checkAndRefresh = () => {
      const token = getAccessToken();
      if (!token) {
        return;
      }

      const expiresIn = getTokenExpiresIn(token);

      // Refresh if token is already expired or expires within buffer period
      if (expiresIn <= REFRESH_BUFFER_MS) {
        performRefresh();
      }
    };

    // Check immediately on mount
    checkAndRefresh();

    // Set up interval for periodic checks
    const intervalId = setInterval(checkAndRefresh, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, performRefresh]);
};

/**
 * useTokenExpiresIn Hook
 *
 * Returns the time remaining until the access token expires.
 * Updates every second when token is about to expire.
 *
 * @returns Time in milliseconds until token expires, or 0 if expired/no token
 */
export const useTokenExpiresIn = (): number => {
  const { isAuthenticated } = useAuthStore();

  const getExpiresIn = useCallback(() => {
    if (!isAuthenticated) return 0;
    const token = getAccessToken();
    if (!token) return 0;
    return getTokenExpiresIn(token);
  }, [isAuthenticated]);

  // Note: This is a simple implementation. For real-time countdown,
  // you would add state and useEffect with interval updates.
  return getExpiresIn();
};
